import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, MapPin, Clock, ExternalLink } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import UpperRoomImage from '../assets/images/upper-room.jpg';
import NewLifeImage from '../assets/images/new-life.jpg';

const Events = () => {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if an event is live based on current time
  const isEventLive = useCallback((event) => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 3 = Wednesday, 6 = Saturday
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    if (event.id === 1) {
      // Upper Room: Wednesday at 7:00 PM
      return currentDay === 3 && currentHour === 19 && currentMinute >= 0;
    }
    if (event.id === 2) {
      // New Life Ministry: Saturday at 12:00 PM or Wednesday at 7:30 PM
      return (
        (currentDay === 6 && currentHour === 12 && currentMinute >= 0) ||
        (currentDay === 3 && currentHour === 19 && currentMinute >= 30)
      );
    }
    return false;
  }, []);

  // Fetch events with language-specific data
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const mockEvents = i18n.language === 'ar' ? [
        {
          id: 1,
          title: ' عليّة الصلاه في كنيسة العطارين بالإسكندرية',
          description: 'شاهد البث المباشر لخدمات عليّة  الصلاه من كنيسة العطارين بالإسكندرية',
          date: 'الأربعاء',
          time: '7:00 مساءً',
          location: 'كنيسة العطارين، الإسكندرية',
          image: UpperRoomImage,
          customSearchUrl: 'https://www.facebook.com/3oleyet.el.SALA.Alex.THE.UPPER.ROOM',
        },
        {
          id: 2,
          title: 'خدمة الحياة الجديدة',
          description: 'شاهد البث المباشر لخدمات الحياة الجديدة يومي السبت والأربعاء',
          date: 'السبت والأربعاء',
          time: 'السبت 12:00 ظهرًا، الأربعاء 7:30 مساءً',
          location: 'خدمة الحياة الجديدة، مصر',
          image: NewLifeImage,
          customSearchUrl: 'https://www.facebook.com/newlifeministryegypt',
        },
      ] : i18n.language === 'es' ? [
        {
          id: 1,
          title: 'Upper Room en la Iglesia de Attareen en Alejandría',
          description: 'Mira la transmisión en vivo de los servicios de Upper Room desde la Iglesia de Attareen en Alejandría',
          date: 'Miércoles',
          time: '7:00 PM',
          location: 'Iglesia de Attareen, Alejandría',
          image: UpperRoomImage,
          customSearchUrl: 'https://www.facebook.com/3oleyet.el.SALA.Alex.THE.UPPER.ROOM',
        },
        {
          id: 2,
          title: 'Ministerio Nueva Vida',
          description: 'Mira la transmisión en vivo de los servicios de Nueva Vida los sábados y miércoles',
          date: 'Sábado y Miércoles',
          time: 'Sábado 12:00 PM, Miércoles 7:30 PM',
          location: 'Ministerio Nueva Vida, Egipto',
          image: NewLifeImage,
          customSearchUrl: 'https://www.facebook.com/newlifeministryegypt',
        },
      ] : [
        {
          id: 1,
          title: 'Upper Room at Attareen Church, Alexandria',
          description: 'Watch live services of Upper Room from Attareen Church in Alexandria',
          date: 'Wednesday',
          time: '7:00 PM',
          location: 'Attareen Church, Alexandria',
          image: UpperRoomImage,
          customSearchUrl: 'https://www.facebook.com/3oleyet.el.SALA.Alex.THE.UPPER.ROOM',
        },
        {
          id: 2,
          title: 'New Life Ministry',
          description: 'Watch live services of New Life Ministry on Saturdays and Wednesdays',
          date: 'Saturday and Wednesday',
          time: 'Saturday 12:00 PM, Wednesday 7:30 PM',
          location: 'New Life Ministry, Egypt',
          image: NewLifeImage,
          customSearchUrl: 'https://www.facebook.com/newlifeministryegypt',
        },
      ];

      // Update events with live status
      const updatedEvents = mockEvents.map(event => ({
        ...event,
        isLive: isEventLive(event),
      }));

      setEvents(updatedEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error(t('events.errorUpdating'), {
        position: 'bottom-right',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [i18n.language, t, isEventLive]);

  // Fetch events on mount and poll every minute
  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 60000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  // Handle event link click
  const handleEventLinkClick = useCallback((event) => {
    const url = event.customSearchUrl || `https://www.google.com/search?q=${encodeURIComponent(event.title)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.info(t('events.openingPage'), {
      position: 'bottom-right',
      duration: 2000,
    });
  }, [t]);

  return (
    <div className="w-full px-4 py-8 md:px-6 lg:px-8 md:py-12 min-h-screen">
      <Toaster />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl font-bold mb-4 text-gray-800">{t('events.joinUs')}</h1>
        <p className="text-xl md:text-1xl lg:text-3xl bg-gradient-to-r from-amber-500 via-orange-600 to-yellow-500 bg-clip-text text-transparent">
          {t('events.latestServices')}
        </p>
      </motion.div>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {loading ? (
            Array(2).fill(0).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse"
              >
                <div className="h-40 bg-gray-200" />
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))
          ) : (
            events.map((event) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.03 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 relative flex flex-col max-w-sm"
              >
                {event.isLive && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 z-10">
                    <span className="animate-pulse h-1.5 w-1.5 bg-white rounded-full" />
                    {t('events.live')}
                  </div>
                )}
                <div className="w-full h-40 overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-4 flex flex-col flex-1 justify-between">
                  <div className="flex flex-col">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{event.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{event.description}</p>
                    <div className="mt-2 space-y-1 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{event.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </div>
                  <button
className="mt-4 w-full hover:bg-gray-700 text-white font-medium px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors duration-300"
style={{ backgroundColor: '#a5a5a5' }}
                    onClick={() => handleEventLinkClick(event)}
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span className="text-xs">{t('events.viewDetails')}</span>
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Events;